import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { config } from '../config.js';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';
import { getLanguageNativeLabel } from '../lib/language-label.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 設定ページ。テーマ切替・言語切替・アカウント情報表示を提供
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects useTheme [calls] テーマフック呼び出し
 * @graph-connects getLanguageNativeLabel [calls] 各言語の自国語表記
 */
export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

/** @graph-connects none */
function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  const themeOptions: { value: 'dark' | 'light' | 'system'; labelKey: string }[] = [
    { value: 'dark', labelKey: 'settings.dark' },
    { value: 'light', labelKey: 'settings.light' },
    { value: 'system', labelKey: 'settings.system' },
  ];

  const languageOptions = config.supportedLanguages.map((value) => ({
    value,
    label: getLanguageNativeLabel(value),
  }));

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      {/* ページヘッダー */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('settings.title')}
        </h1>
      </div>

      {/* テーマ設定 */}
      <div className="glass-card p-6">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          {t('settings.theme')}
        </h2>
        <div className="flex gap-3">
          {themeOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setTheme(opt.value);
              }}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  theme === opt.value
                    ? 'bg-[var(--color-accent-primary)] text-white'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                }
              `}
              aria-label={t(opt.labelKey)}
              aria-pressed={theme === opt.value}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* 言語設定 */}
      <div className="glass-card p-6">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          {t('settings.language')}
        </h2>
        <div className="flex gap-3">
          {languageOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                void i18n.changeLanguage(opt.value);
              }}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  i18n.language.split('-')[0] === opt.value
                    ? 'bg-[var(--color-accent-primary)] text-white'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                }
              `}
              lang={opt.value}
              aria-label={opt.label}
              aria-pressed={i18n.language.split('-')[0] === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* アカウント情報 */}
      <div className="glass-card p-6">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          {t('settings.account')}
        </h2>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-[var(--color-text-muted)]">{t('settings.name')}</p>
            <p className="text-sm text-[var(--color-text-primary)]">{user?.name ?? '-'}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--color-text-muted)]">{t('settings.email')}</p>
            <p className="text-sm text-[var(--color-text-primary)]">{user?.email ?? '-'}</p>
          </div>
          {user?.portalRole && (
            <div>
              <p className="text-xs text-[var(--color-text-muted)]">{t('settings.role')}</p>
              <p className="text-sm text-[var(--color-text-primary)]">{user.portalRole}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
